import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Calendar as AntCalendar, CalendarProps, ConfigProvider } from 'antd';
import { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useStore } from 'zustand';

// eslint-disable-next-line no-restricted-imports
import { useCreateEventModal } from 'features/calendarEvent/create';
import { isHasAccess } from 'features/validateAccess';
import { calendarService, calendarStore } from 'entities/calendar';

import { withSuspense } from 'shared/lib/react';
import { Loader } from 'shared/ui/Loader';
import { UPDATE_CALENDAR } from '../../CalendarsLayout.permissions';
import { dayjs, setDayjsLocale, ruRU, enUS } from '../../config/dayjs';
import { СalendarHeaderRender } from '../CalendarHeader';
import { DayCell } from '../DayCell';
import { MonthCell } from '../MonthCell';
import styles from './CalendarView.module.scss';

const today = dayjs();

const CalendarView = (): React.ReactElement => {
  const { i18n } = useTranslation();
  const { calendarId } = useParams();
  const dayRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});

  const openedEventId = useStore(calendarStore, (state) => state.openedEventId);
  const selectYear = useStore(calendarStore, (state) => state.selectYear);
  const selectMonth = useStore(calendarStore, (state) => state.selectMonth);
  const selectedYear = useStore(calendarStore, (state) => state.selectedYear);
  const selectedViewType = useStore(calendarStore, (state) => state.selectedViewType);

  const [selectedDate, setSelectedDate] = useState(dayjs(`${selectedYear}-${today.month() + 1}-${today.date()}`));
  const [selectedRange, setSelectedRange] = useState<{ start: Dayjs | null; end: Dayjs | null }>({
    start: null,
    end: null,
  });

  const cleanSelectedRange = useCallback(() => {
    setSelectedRange({ start: null, end: null });
  }, []);

  const canUpdateCalendarEvent = isHasAccess(UPDATE_CALENDAR);
  const openCreateEventModal = useCreateEventModal(calendarId as string, cleanSelectedRange);

  useEffect(() => {
    setDayjsLocale(i18n.language);
    setSelectedDate((prev) => dayjs(prev.format('YYYY-MM-DD')));
  }, [i18n.language]);

  useEffect(() => {
    if (openedEventId?.clickedDate && dayRefs.current[openedEventId.clickedDate]) {
      dayRefs.current[openedEventId.clickedDate]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [openedEventId]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        setSelectedRange({ start: null, end: null });
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const { data: calendar } = useSuspenseQuery(
    calendarService.queryOptions({
      id: calendarId as string,
      year: selectedYear.toString(),
    }),
  );

  const eventsByDate = useMemo(() => {
    if (!calendar?.events) return new Map();

    const eventMap = new Map();

    calendar.events.forEach((event) => {
      const start = dayjs(event.startDate);
      const end = dayjs(event.endDate);
      let current = start;

      while (current.isBefore(end) || current.isSame(end, 'day')) {
        const dateKey = current.format('YYYY-MM-DD');
        if (!eventMap.has(dateKey)) {
          eventMap.set(dateKey, []);
        }
        eventMap.get(dateKey).push(event);
        current = current.add(1, 'day');
      }
    });

    return eventMap;
  }, [calendar?.events]);

  const isRangeBlocked = useCallback(
    (start: Dayjs, end: Dayjs): boolean => {
      if (end.isBefore(start, 'day') || end.year() !== start.year()) {
        return true;
      }
      let current = start.isBefore(end) ? start : end;
      const last = start.isAfter(end) ? start : end;
      while (current.isBefore(last) || current.isSame(last, 'day')) {
        const key = current.format('YYYY-MM-DD');
        if (eventsByDate.has(key) && eventsByDate.get(key).length > 0) {
          return true;
        }
        current = current.add(1, 'day');
      }
      return false;
    },
    [eventsByDate],
  );

  useEffect(() => {
    if (selectedRange.start && selectedRange.end && canUpdateCalendarEvent) {
      openCreateEventModal([selectedRange.start, selectedRange.end]);
    }
  }, [selectedRange, openCreateEventModal, canUpdateCalendarEvent]);

  const onSelect = useCallback(
    (day: Dayjs): void => {
      const dateKey = day.format('YYYY-MM-DD');
      if (eventsByDate.has(dateKey)) {
        return;
      }

      setSelectedRange((prev) => {
        if (!prev.start) {
          return { start: day, end: null };
        }
        if (!isRangeBlocked(prev.start, day)) {
          return { start: prev.start, end: day };
        }
        return prev;
      });
    },
    [eventsByDate, isRangeBlocked],
  );

  const onPanelChange = useCallback(
    (newValue: Dayjs): void => {
      setSelectedDate((prev) => newValue.date(prev.date()));
      selectYear(newValue.year());
      selectMonth(newValue.month());
    },
    [selectYear, selectMonth],
  );

  const cellRender: CalendarProps<Dayjs>['cellRender'] = (current, info) => {
    switch (info.type) {
      case 'date': {
        const dateKey = current.format('YYYY-MM-DD');
        const dayEvents = eventsByDate.get(dateKey) || [];

        let isSelected = false;
        if (selectedRange && selectedRange.start && selectedRange.end) {
          isSelected = current.isBetween(selectedRange.start, selectedRange.end, 'day', '[]');
        } else if (selectedRange && selectedRange.start && !selectedRange.end) {
          isSelected = current.isSame(selectedRange.start, 'day');
        }

        return (
          <DayCell
            date={current}
            dayEvents={dayEvents}
            isBlocked={!!selectedRange.start && !selectedRange.end && isRangeBlocked(selectedRange.start, current)}
            isSelected={isSelected}
            ref={(el) => {
              if (el) {
                dayRefs.current[dateKey] = el;
              } else {
                delete dayRefs.current[dateKey];
              }
            }}
          />
        );
      }
      case 'month': {
        return (
          <MonthCell
            date={current}
            events={calendar?.events || []}
            onSelect={onSelect}
            selectedRange={selectedRange}
            isRangeBlocked={isRangeBlocked}
          />
        );
      }
      default:
        return info.originNode;
    }
  };

  return (
    <ConfigProvider locale={i18n.language === 'ru' ? ruRU : enUS}>
      <AntCalendar
        value={selectedDate}
        onSelect={(date, info) => {
          if (info && info.source === 'customize') {
            selectMonth(date.month());
            return;
          }
          if (info && info.source === 'month') {
            return;
          }
          if (date.month() === selectedDate.month() && canUpdateCalendarEvent) {
            onSelect(date);
          }
        }}
        headerRender={СalendarHeaderRender(`${calendar?.name}, ${selectedYear}`)}
        onPanelChange={onPanelChange}
        cellRender={cellRender}
        mode={selectedViewType}
        className={styles.yearViewWrapper}
      />
    </ConfigProvider>
  );
};
export default withSuspense(CalendarView, { fallback: <Loader /> });
